import "~/pages/chat-agent/ChatPage.scss";
import type React from "react";
import {useEffect, useRef} from "react";
import {observer} from "mobx-react-lite";
import {IconButton} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {useStores} from "~/stores";
import {useChatContext} from "~/pages/chat-agent/context/ChatContext";
import {ChatArea} from "~/pages/chat-agent/components/ChatArea/ChatArea";
import type {EmbeddedDashboardView} from "~/pages/chat-agent/types/chatTypes";
import {embeddedDashboardKey} from "~/pages/chat-agent/utils/embeddedDashboardKey";

export type AtlasPanelMode =
    | {kind: "new"}
    | {kind: "edit"; sessionId: string};

type Props = {
    mode: AtlasPanelMode;
    onClose: () => void;
    /** Atlas created or changed a dashboard in this conversation; the page shows it on the right. */
    onDashboardChanged?: (dashboard: EmbeddedDashboardView) => void;
};

const dashboardKey = (d: EmbeddedDashboardView | null) => (d ? embeddedDashboardKey(d) : null);

/**
 * Left-side Atlas panel of the Dashboards module (Mithra 3.0 "Edit with Atlas"): the regular chat,
 * docked before the gallery or a dashboard in DashboardsAtlasSplit, which owns its width. New mode
 * starts a fresh Atlas chat, which hands dashboard requests to its DashGen sub-agent (the API refuses
 * chats of DashGen's own); edit mode restores the conversation that built the dashboard.
 */
export const AtlasDashboardPanel: React.FC<Props> = observer(({mode, onClose, onDashboardChanged}) => {
    const {bagStore} = useStores();
    const {
        embeddedDashboard, isRestoringSession, restoreSession, setChatDocked, startNewChat,
    } = useChatContext();
    // Last dashboard state already reported; undefined = take the first one seen after a restore.
    const baseline = useRef<string | null | undefined>(undefined);
    const startedFor = useRef<string | null>(null);
    const modeKey = mode.kind === "new" ? "new" : `edit:${mode.sessionId}`;

    // The panel's chat never takes over the URL (that would navigate to the chat page).
    useEffect(() => {
        setChatDocked(true);
        return () => setChatDocked(false);
    }, [setChatDocked]);

    useEffect(() => {
        if (startedFor.current === modeKey) return;
        startedFor.current = modeKey;
        if (mode.kind === "new") {
            // Whatever the context still shows belongs to an earlier chat: only a different one is news.
            baseline.current = dashboardKey(embeddedDashboard);
            // Fresh Atlas chat; the session is created with the first message.
            startNewChat(undefined, bagStore.bagId);
        } else {
            baseline.current = undefined;
            void restoreSession(mode.sessionId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- start once per mode
    }, [modeKey]);

    useEffect(() => {
        if (isRestoringSession) return;
        const key = dashboardKey(embeddedDashboard);
        if (baseline.current === undefined) {
            baseline.current = key;
            return;
        }
        if (!embeddedDashboard || key === baseline.current) return;
        baseline.current = key;
        onDashboardChanged?.(embeddedDashboard);
    }, [embeddedDashboard, isRestoringSession, onDashboardChanged]);

    const title = mode.kind === "new" ? "Create with Atlas" : "Edit with Atlas";

    return (
        <aside className="dsh-root dsh-atlas-panel" aria-label={title}>
            {/* No head strip: the close button floats over the chat's top corner. */}
            <IconButton size="small" className="dsh-atlas-close" aria-label="Close Atlas panel" onClick={onClose}>
                <CloseIcon fontSize="small"/>
            </IconButton>
            <div className="chat-page dsh-atlas-chat">
                {/* Greeting and starter prompts follow the route (useChatSurface). */}
                <ChatArea docked/>
            </div>
        </aside>
    );
});
